import { toast } from 'react-toastify';
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from 'react-redux';
import React, { useState, useEffect, useCallback } from 'react';
import { Button, Chip, CircularProgress, Alert, TextField, Box, Checkbox, FormControl, Select, MenuItem } from '@mui/material';
import { CloudUpload as UploadIcon, FilterAlt as FilterIcon, People as AudienceIcon, Email as EmailIcon, Phone as PhoneIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, AutoMode as AutoModeIcon, Search as SearchIcon } from '@mui/icons-material';

import ImportWizard from '../../components/ImportWizard';
import { buildMongoQuery } from '../../utils/filterConfig';
import AdvancedFilterModal from '../../components/Filters/AdvancedFilter';
import { getObjects, setFilters, applyFilters, getProperties } from '../../store/Objects';
import { Link } from 'react-router-dom';
import TableAnimationFiles from '../../components/Animations/TableAnimationFiles.js';
import { fontWeight } from '../../../node_modules/@mui/system/index';
import { AutoAwesome as AIVariablesIcon } from '@mui/icons-material';
import AIVariablesDrawer from './components/AIVariablesDrawer';
import { getAIVariableColumns } from '../../store/Campaign';


const AudienceStep = ({ formData, handleChange, campaignId }) => {
  const dispatch = useDispatch();

  // Redux state
  const contacts = useSelector(state => state['Objects']?.['contacts']?.['records']) || [];
  const contactsLoading = useSelector(state => state['Objects']?.['contacts']?.['loading']) || false;
  const totalContacts = useSelector(state => state['Objects']?.['contacts']?.['pagination']?.['total']) || 0;
  const contactStats = useSelector(state => state['Objects']?.['contacts']?.['contactStats']);
  const activeFilters = useSelector(state => state['Objects']?.['contacts']?.['filters']) || { filterGroups: [], groupOperator: 'OR' };
  const savedAiColumns = useSelector(state => state['Campaign']?.['aiVariablesColumns']) || {};

  // Local state
  const [searchQuery, setSearchQuery] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationResetToggle, setPaginationResetToggle] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [importWizardOpen, setImportWizardOpen] = useState(false);
  const [showContactsPreview, setShowContactsPreview] = useState(true);
  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleCleared, setToggleCleared] = useState(false);
  const [showAIDrawer, setShowAIDrawer] = useState(false);
  const [aiColumns, setAiColumns] = useState({}); // { columnName: { contactId: value } }
  const [audienceTypeOpen, setAudienceTypeOpen] = useState(false);

  const selectedCount = selectedRows.length;

  // Computed values
  const allFilters = activeFilters?.['filterGroups']?.flatMap(g => (g['filters'] || []).map(f => ({ ...f, groupId: g['id'] }))) || [];
  const withEmail = contactStats?.['withEmail'] ?? contacts.filter(c => c['email']).length;
  const withPhone = contactStats?.['withPhone'] ?? contacts.filter(c => c['phone']).length;

  // table stats
  const headerScrollHeight = 'calc(100vh - 250px)';
  const responsiveClass = 'datatable--paginated contact-table';


  const columns = [
    {
      name: "Name",
      selector: (row) => `${row.firstName || ""} ${row.lastName || ""}`,
      cell: (row) => (
        <span className="contact-name">
          {row.firstName || ""} {row.lastName || ""}
          {!row.firstName && !row.lastName && (
            <span className="no-name">No name</span>
          )}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Email",
      selector: (row) => row.email,
      cell: (row) => row.email || <span className="no-data">-</span>,
      sortable: true,
    },
    {
      name: "Phone",
      selector: (row) => row.phone,
      cell: (row) => row.phone || <span className="no-data">-</span>,
    },
    {
      name: "Company",
      selector: (row) => row.company,
      cell: (row) => row.company || <span className="no-data">-</span>,
    },
    // Dynamic AI Variable columns
    ...Object.keys(aiColumns).map(colName => ({
      name: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <AIVariablesIcon style={{ fontSize: 13, color: 'var(--primary-color)' }} />
          {colName}
        </span>
      ),
      selector: (row) => aiColumns[colName]?.[String(row._id)] || '',
      cell: (row) => {
        const val = aiColumns[colName]?.[String(row._id)];
        return val
          ? <span className="aiv-cell-value" title={val}>{val.length > 60 ? val.slice(0, 60) + '…' : val}</span>
          : <span className="no-data">-</span>;
      },
      grow: 2,
    })),
  ];

  // Load contacts and set default type to segment
  useEffect(() => {
    dispatch(getProperties());

    // Set default audience type to segment if not already set
    if (!formData['audience']?.['type'] || formData['audience']['type'] === 'crm')
      handleChange('audience', { ...formData['audience'], type: 'segment', autoEnroll: true });

    // Restore filters from saved campaign if available and apply them to API call
    if (formData['audience']?.['filterDefinition']) {
      dispatch(setFilters({ objectType: 'contacts', filters: formData['audience']['filterDefinition'] }));
      const savedFilters = formData['audience']['filterDefinition'];
      const mongoQuery = buildMongoQuery(savedFilters['filterGroups'], savedFilters['groupOperator']);
      dispatch(getObjects({ objectType: 'contacts', limit: 50, page: 1, filters: mongoQuery }));
    } else dispatch(getObjects({ objectType: 'contacts', limit: 50, page: 1 }));

    // Load saved AI variable columns for this campaign
    if (campaignId) dispatch(getAIVariableColumns({ campaignId }));
  }, []);

  // Sync saved AI columns from Redux into local state (for display in table)
  useEffect(() => {
    if (savedAiColumns && Object.keys(savedAiColumns).length > 0) {
      setAiColumns(savedAiColumns);
    }
  }, [savedAiColumns]);

  // Filter handlers
  const handleRemoveFilter = useCallback((groupId, filterId) => {
    const updatedGroups = activeFilters['filterGroups'].map(group => {
      if (group['id'] === groupId) return { ...group, filters: group['filters'].filter(f => f['id'] !== filterId) };
      else return group;
    }).filter(group => group['filters'] && group['filters'].length > 0);

    const updatedFilters = { ...activeFilters, filterGroups: updatedGroups };
    dispatch(setFilters({ objectType: 'contacts', filters: updatedFilters }));

    // Build MongoDB query and fetch contacts with updated filters
    const mongoQuery = buildMongoQuery(updatedFilters['filterGroups'], updatedFilters['groupOperator']);
    dispatch(getObjects({ objectType: 'contacts', limit: 50, page: 1, filters: mongoQuery }));

    // Also update formData so changes persist when moving to next step
    handleChange('audience', { ...formData['audience'], filters: mongoQuery, filterDefinition: updatedFilters });
  }, [activeFilters, dispatch, formData, handleChange]);

  const handleClearFilters = useCallback(() => {
    const emptyFilters = { filterGroups: [], groupOperator: 'OR' };
    dispatch(setFilters({ objectType: 'contacts', filters: emptyFilters }));
    dispatch(getObjects({ objectType: 'contacts', limit: 50, page: 1 }));
    // Also clear filters from formData so they persist when moving to next step
    handleChange('audience', { ...formData['audience'], filters: {}, filterDefinition: emptyFilters });
  }, [dispatch, formData, handleChange]);

  const handleFilterApply = useCallback(() => {
    const mongoQuery = buildMongoQuery(activeFilters['filterGroups'], activeFilters['groupOperator']);
    setPaginationResetToggle(prev => !prev);
    handleChange('audience', { ...formData['audience'], filters: mongoQuery, totalCount: totalContacts, filterDefinition: activeFilters });
  }, [activeFilters, formData, handleChange, totalContacts]);

  // Server-side pagination handlers
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    const mongoQuery = formData['audience']?.['filterDefinition'] ? buildMongoQuery(activeFilters['filterGroups'], activeFilters['groupOperator']) : {};
    dispatch(getObjects({ objectType: 'contacts', limit: rowsPerPage, page, filters: mongoQuery }));
  }, [dispatch, rowsPerPage, activeFilters, formData]);

  const handlePerRowsChange = useCallback((newPerPage, page) => {
    setRowsPerPage(newPerPage);
    const mongoQuery = formData['audience']?.['filterDefinition'] ? buildMongoQuery(activeFilters['filterGroups'], activeFilters['groupOperator']) : {};
    dispatch(getObjects({ objectType: 'contacts', limit: newPerPage, page, filters: mongoQuery }));
  }, [dispatch, activeFilters, formData]);

  // CSV Import handlers
  const handleImportComplete = useCallback((results) => {
    setImportWizardOpen(false);
    if (results?.['jobId']) {
      toast.success(`Imported ${results['created']?.length || 0} contacts!`);
      dispatch(getObjects({ objectType: 'contacts', limit: rowsPerPage, page: 1 }));
    }
  }, [dispatch, rowsPerPage]);

  // Search handler
  const handleSearch = useCallback((e) => {
    const query = e['target']['value'];
    setSearchQuery(query);
    if (query.trim()) dispatch(getObjects({ objectType: 'contacts', limit: rowsPerPage, page: 1, search: query }));
    else dispatch(getObjects({ objectType: 'contacts', limit: rowsPerPage, page: 1 }));
  }, [dispatch, rowsPerPage]);

  return (
    <div className="wizard-step-content audience-step">
      <h2 style={{ marginBottom: '10px', fontWeight: 700 }}>Select Your Audience</h2>
      {/* Summary Stats */}
      <div className="audience-summary-bar">
        <div className="summary-stat">
          <svg width="13" height="10" viewBox="0 0 13 10" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2.96094 6.10156C3.69275 5.81661 4.49798 5.74733 5.27246 5.90234C5.95 6.03798 6.57513 6.33945 7.08789 6.77344L7.30078 6.96777C7.73117 7.39154 8.04883 7.90525 8.23047 8.46582L8.23242 8.47168C8.34441 8.80474 8.40347 9.15108 8.41113 9.5H0.503906C0.522308 8.78698 0.750052 8.09124 1.16895 7.49512C1.60598 6.8733 2.22923 6.38651 2.96094 6.10156ZM3.70605 0.642578C4.0635 0.500721 4.45807 0.463655 4.83887 0.536133C5.21954 0.608627 5.56659 0.787114 5.83691 1.0459C6.10703 1.30461 6.28904 1.63175 6.3623 1.98438C6.43552 2.33685 6.39808 2.70266 6.25391 3.03613C6.10963 3.36981 5.86369 3.65857 5.54395 3.86328C5.2239 4.06814 4.84484 4.17773 4.45605 4.17773C3.9346 4.17709 3.43868 3.97903 3.07617 3.63184C2.71442 3.28532 2.51535 2.82023 2.51465 2.33984C2.51458 1.98099 2.62535 1.62822 2.83594 1.32617C3.04677 1.02382 3.34867 0.784434 3.70605 0.642578Z" stroke="#EF4D56" />
            <path d="M9.61523 1.90405C10.3623 1.90422 10.9304 2.47897 10.9307 3.14331C10.9307 3.80782 10.3624 4.38338 9.61523 4.38354C8.86791 4.38354 8.29883 3.80792 8.29883 3.14331C8.29906 2.47888 8.86806 1.90405 9.61523 1.90405Z" stroke="#EF4D56" />
            <path d="M9.6145 5.8623C10.3384 5.86308 11.0288 6.13871 11.5344 6.62305C12.0166 7.08494 12.29 7.7001 12.3147 8.3418H10.1223C10.0874 8.19674 10.0451 8.05285 9.99731 7.91113C9.76385 7.19355 9.38204 6.53173 8.87817 5.96289C9.11623 5.89751 9.36396 5.8634 9.6145 5.8623Z" stroke="#EF4D56" />
          </svg>
          <span>{totalContacts.toLocaleString()} contacts</span>
        </div>
        <div className="summary-stat">
          <svg width="13" height="9" viewBox="0 0 13 9" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11.675 0H1.125C0.50595 0 0 0.503725 0 1.125V7.875C0 8.4965 0.506275 9 1.125 9H11.675C12.2941 9 12.8 8.49627 12.8 7.875V1.125C12.8 0.503575 12.2938 0 11.675 0ZM11.5022 0.75L6.6906 5.57735C6.544 5.7244 6.25608 5.7245 6.1094 5.57735L1.29775 0.75H11.5022ZM0.75 7.73712V1.26287L3.97663 4.5L0.75 7.73712ZM1.29775 8.25L4.5061 5.0312L5.57823 6.10683C6.01757 6.5476 6.7826 6.54743 7.2218 6.10683L8.29393 5.03122L11.5022 8.25H1.29775ZM12.05 7.73712L8.82337 4.5L12.05 1.26287V7.73712Z" fill="#120348" />
          </svg>
          <span>{withEmail.toLocaleString()} with email</span>
        </div>
        <div className="summary-stat">
          <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2.03427 6.69824C3.02815 7.88631 4.22456 8.82172 5.5901 9.48361C6.11001 9.72999 6.8053 10.0223 7.57995 10.0724C7.62797 10.0745 7.6739 10.0766 7.72193 10.0766C8.24184 10.0766 8.65943 9.89703 8.99977 9.52746C9.00186 9.52537 9.00604 9.5212 9.00812 9.51702C9.12923 9.37086 9.26703 9.23932 9.41111 9.09942C9.50924 9.00546 9.60946 8.90733 9.70551 8.80711C10.1503 8.34357 10.1503 7.75476 9.70133 7.30585L8.44646 6.05097C8.23348 5.82964 7.97875 5.71272 7.71149 5.71272C7.44423 5.71272 7.1874 5.82965 6.96817 6.04888L6.22067 6.79638C6.15177 6.75671 6.08077 6.72121 6.01396 6.6878C5.93044 6.64605 5.85318 6.60637 5.78428 6.56253C5.1036 6.13031 4.48556 5.56656 3.89466 4.84203C3.59608 4.4641 3.39563 4.14673 3.25574 3.82309C3.45201 3.64562 3.63575 3.45979 3.81323 3.27813C3.87587 3.2134 3.94059 3.14868 4.00532 3.08395C4.23082 2.85845 4.35193 2.59745 4.35193 2.33228C4.35193 2.0671 4.23291 1.8061 4.00532 1.5806L3.3831 0.958384C3.31002 0.885304 3.24112 0.814313 3.17013 0.741233C3.03232 0.599251 2.88825 0.453092 2.74627 0.321549C2.53121 0.110663 2.27856 0 2.0113 0C1.74612 0 1.49139 0.110663 1.26798 0.323637L0.487072 1.10454C0.203106 1.38851 0.0423316 1.73302 0.00892385 2.13183C-0.0307478 2.63086 0.0611234 3.1612 0.299153 3.80221C0.66455 4.79401 1.21578 5.71481 2.03427 6.69824ZM0.518391 2.17568C0.543447 1.89798 0.649934 1.66621 0.85038 1.46576L1.62711 0.689034C1.74821 0.572107 1.88184 0.511555 2.0113 0.511555C2.13867 0.511555 2.26812 0.572107 2.38714 0.69321C2.52703 0.822665 2.65857 0.958384 2.80056 1.10245C2.87155 1.17553 2.94463 1.24861 3.01771 1.32378L3.63992 1.946C3.76938 2.07545 3.83619 2.207 3.83619 2.33645C3.83619 2.46591 3.76938 2.59745 3.63992 2.7269C3.5752 2.79163 3.51047 2.85845 3.44574 2.92317C3.25156 3.11944 3.06991 3.30527 2.86946 3.48275L2.85902 3.49319C2.68572 3.6665 2.71286 3.83145 2.75462 3.95673C2.75671 3.96299 2.7588 3.96717 2.76088 3.97343C2.92166 4.35971 3.14507 4.72719 3.49377 5.16567C4.12016 5.93822 4.77996 6.53747 5.50658 6.99891C5.59636 7.05738 5.69241 7.10331 5.78219 7.14925C5.86571 7.19101 5.94297 7.23068 6.01187 7.27453C6.02022 7.2787 6.02649 7.28288 6.03484 7.28706C6.10374 7.32255 6.17056 7.33926 6.23737 7.33926C6.40441 7.33926 6.51299 7.23277 6.54848 7.19727L7.32939 6.41637C7.45049 6.29526 7.58203 6.23054 7.71149 6.23054C7.87017 6.23054 7.99963 6.32867 8.08106 6.41637L9.34011 7.67333C9.59067 7.92389 9.58858 8.19533 9.33385 8.4605C9.24615 8.55446 9.15428 8.64424 9.05615 8.7382C8.90999 8.88019 8.75757 9.02635 8.61976 9.1913C8.37964 9.4502 8.09359 9.57131 7.72402 9.57131C7.68852 9.57131 7.65094 9.56922 7.61544 9.56713C6.93058 9.52328 6.29375 9.25602 5.8156 9.02843C4.51688 8.39995 3.37684 7.50838 2.43098 6.3767C1.65217 5.43919 1.12808 4.56642 0.781477 3.631C0.566415 3.0568 0.484984 2.59536 0.518391 2.17568Z" fill="#F7931A" />
          </svg>
          <span>{withPhone.toLocaleString()} with phone</span>
        </div>
        {/* <Button
          size="small"
          variant="text"
          onClick={() => setShowContactsPreview(!showContactsPreview)}
          endIcon={showContactsPreview ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        >
          {showContactsPreview ? 'Hide' : 'Show'}
        </Button> */}
      </div>

      <div className="audience-step__type-alert">
        {formData['audience']?.['autoEnroll'] ? (
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.5625 5.24706H9.1881C8.94662 5.24706 8.75064 5.05117 8.75064 4.8098C8.75064 4.56843 8.97942 4.39965 9.16623 4.21339L10.3819 2.99826C9.46894 2.22429 8.29134 1.75294 7 1.75294C4.39982 1.75294 2.24673 3.64495 1.8294 6.1255H0.0612386C0.492562 2.67555 3.4313 0.00393295 7 0.00393295C8.77471 0.00393295 10.3924 0.666813 11.6247 1.756L12.8779 0.503277C13.1137 0.267598 13.3211 0 13.5625 0C13.804 0 14 0.19589 14 0.437263V4.8098C14 4.86271 14 5.24706 13.5625 5.24706ZM0.437431 8.75296H4.81188C5.05336 8.75296 5.24934 8.94885 5.24934 9.19022C5.24934 9.4316 5.02056 9.60037 4.83375 9.78663L3.61809 11.0018C4.53104 11.7757 5.70864 12.2471 6.99998 12.2471C9.60016 12.2471 11.7533 10.3551 12.1706 7.87453H13.9387C13.5074 11.3244 10.5687 13.9961 7 13.9961C5.22527 13.9961 3.60761 13.3332 2.37533 12.244L1.12204 13.4967C0.886255 13.7324 0.678909 14 0.437431 14C0.195954 14 0 13.8041 0 13.5627V9.1902C0 9.13729 0 8.75296 0.437431 8.75296Z" fill="currentColor" /></svg>
        ) : (
          <svg width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 6H9.5V4C9.5 1.794 7.706 0 5.5 0C3.294 0 1.5 1.794 1.5 4V6H1C0.448 6 0 6.448 0 7V13C0 13.552 0.448 14 1 14H10C10.552 14 11 13.552 11 13V7C11 6.448 10.552 6 10 6ZM3 4C3 2.622 4.122 1.5 5.5 1.5C6.878 1.5 8 2.622 8 4V6H3V4ZM6.25 10.396V11.5C6.25 11.914 5.914 12.25 5.5 12.25C5.086 12.25 4.75 11.914 4.75 11.5V10.396C4.296 10.145 4 9.662 4 9.125C4 8.296 4.671 7.625 5.5 7.625C6.329 7.625 7 8.296 7 9.125C7 9.662 6.704 10.145 6.25 10.396Z" fill="currentColor"/></svg>
        )}
        <span>
          <strong>{formData['audience']?.['autoEnroll'] ? 'Dynamic Audience' : 'Static Audience'}</strong>
          {' - '}
          {formData['audience']?.['autoEnroll']
            ? 'New contacts matching your filters will be automatically enrolled in this campaign.'
            : 'Only the contacts selected at the time of setup are enrolled. New contacts will not be enrolled.'}
        </span>
      </div>

      <div className='content'>
        <div className='main-container'>
          <div className='bottom-content-area'>
            <div className='tabs-content'>
              <div className='content'>
                <div className='allemails-area'>
                  <div className='workflow-table-box'>
                    {/* Search and Filter Bar */}
                    <div className="table-container filters-table workflow-table">
                      <div className='header-table-filters advanced-table-filters'>
                        <div className='left-area'>
                          <div className='filter-box'>
                            <div className='searchbox'>
                              <TextField
                                fullWidth
                                hiddenLabel
                                size="small"
                                name='search'
                                variant="standard"
                                value={searchQuery}
                                onChange={handleSearch}
                                placeholder="Search contacts..."
                              />
                              <i className='icon'>
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M5.82319 10.6464C3.16455 10.6464 1 8.48183 1 5.82319C1 3.16455 3.16455 1 5.82319 1C8.48183 1 10.6464 3.16455 10.6464 5.82319C10.6464 8.48183 8.48183 10.6464 5.82319 10.6464ZM5.82319 1.70583C3.55041 1.70583 1.70583 3.55511 1.70583 5.82319C1.70583 8.09127 3.55041 9.94055 5.82319 9.94055C8.09597 9.94055 9.94055 8.09127 9.94055 5.82319C9.94055 3.55511 8.09597 1.70583 5.82319 1.70583Z" fill="#505258" stroke="#505258" stroke-width="0.236842" />
                                  <path d="M13.3182 13.7068C13.2245 13.7069 13.1307 13.6725 13.0566 13.5985L9.10425 9.63167C8.96097 9.48877 8.96065 9.25192 9.10355 9.10864C9.24646 8.96536 9.4833 8.96505 9.62658 9.10795L13.5789 13.0748C13.7222 13.2177 13.7225 13.4546 13.5796 13.5979C13.5057 13.672 13.412 13.7066 13.3182 13.7068Z" fill="#505258" stroke="#505258" stroke-width="0.236842" />
                                </svg>
                              </i>
                            </div>
                            <div className='filter-box'>
                              <FormControl size="small" >
                                <Select
                                  className="audience-type-dropdown mr-3"
                                  value={formData['audience']?.['autoEnroll'] ? 'dynamic' : 'static'}
                                  onChange={(e) => handleChange('audience', { ...formData['audience'], autoEnroll: e.target.value === 'dynamic' })}
                                  size="small"
                                >
                                  <MenuItem value="dynamic">Dynamic Audience</MenuItem>
                                  <MenuItem value="static">Static Audience</MenuItem>
                                </Select>
                              </FormControl>
                            </div>
                            {/* <button
                              size="small"
                              className="btn-style-edit-tablelist"
                              onClick={() => setShowFilterModal(true)}
                            >
                              <i className="icon">
                                <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M1.74735 8.70926H1.80211L3.26072 8.57262C3.46483 8.55238 3.654 8.46129 3.79836 8.31453L9.30425 2.71753C9.56312 2.45438 9.70749 2.1052 9.70749 1.73578C9.70749 1.36636 9.56312 1.01718 9.30425 0.754026L8.9508 0.394725C8.43307 -0.131575 7.53201 -0.131575 7.01428 0.394725L6.31236 1.10827L1.51337 5.98667C1.369 6.13342 1.2794 6.32572 1.26446 6.53321L1.13005 8.01596C1.11512 8.2032 1.17983 8.38538 1.30927 8.52202C1.42874 8.64347 1.58307 8.70926 1.74735 8.70926ZM7.98503 0.743905C8.14433 0.743905 8.30363 0.804632 8.42311 0.931147L8.77656 1.29045C8.83472 1.34857 8.88092 1.41788 8.91245 1.49434C8.94398 1.5708 8.96022 1.65287 8.96022 1.73578C8.96022 1.81868 8.94398 1.90076 8.91245 1.97722C8.88092 2.05368 8.83472 2.12299 8.77656 2.18111L8.33848 2.62644L7.10887 1.37648L7.54695 0.931147C7.66642 0.809693 7.82573 0.743905 7.98503 0.743905ZM2.00621 6.60406C2.00621 6.57369 2.02115 6.54839 2.04106 6.52815L6.5762 1.9129L7.80581 3.16286L3.27068 7.77305C3.27068 7.77305 3.22089 7.80847 3.196 7.80847L1.88674 7.92993L2.00621 6.599V6.60406ZM10.7031 10.3236C10.7031 10.5311 10.5339 10.7031 10.3298 10.7031H0.373365C0.169259 10.7031 0 10.5311 0 10.3236C0 10.1161 0.169259 9.94404 0.373365 9.94404H10.3298C10.5339 9.94404 10.7031 10.1161 10.7031 10.3236Z" fill="#120348" />
                                </svg>
                              </i>
                            </button> */}

                            {selectedCount > 0 && (
                              <button
                                type="button"
                                className="btn-style-edit-tablelist"
                                onClick={() => {
                                  // ids nikal lo
                                  const ids = selectedRows.map(r => r._id);

                                  // yahan apna delete action call karo
                                  // dispatch(deleteContacts(ids))

                                  // selection clear kar do
                                  setToggleCleared(prev => !prev);
                                  setSelectedRows([]);
                                }}
                                title={`Remove (${selectedCount})`}
                              >
                                <i className="icon">
                                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M5.58426 1.21875H7.41509C7.53263 1.21875 7.63501 1.21875 7.73142 1.23392C7.91923 1.264 8.09738 1.33763 8.25163 1.44889C8.40589 1.56016 8.53196 1.70599 8.61976 1.87471C8.66526 1.96137 8.69722 2.05833 8.73459 2.16938L8.79472 2.35083C8.8362 2.49825 8.92643 2.62726 9.05068 2.71678C9.17494 2.80631 9.32587 2.85107 9.47884 2.84375H11.1038C11.2116 2.84375 11.3149 2.88655 11.3911 2.96274C11.4673 3.03892 11.5101 3.14226 11.5101 3.25C11.5101 3.35774 11.4673 3.46108 11.3911 3.53726C11.3149 3.61345 11.2116 3.65625 11.1038 3.65625H1.89551C1.78776 3.65625 1.68443 3.61345 1.60825 3.53726C1.53206 3.46108 1.48926 3.35774 1.48926 3.25C1.48926 3.14226 1.53206 3.03892 1.60825 2.96274C1.68443 2.88655 1.78776 2.84375 1.89551 2.84375H3.56926C3.71394 2.84028 3.8537 2.79054 3.96805 2.70183C4.0824 2.61312 4.16531 2.49011 4.20463 2.35083L4.2653 2.16938C4.30213 2.05833 4.33409 1.96137 4.37905 1.87471C4.4669 1.70592 4.59306 1.56006 4.74741 1.44878C4.90176 1.33751 5.08003 1.26392 5.26792 1.23392C5.36434 1.21875 5.46672 1.21875 5.58372 1.21875H5.58426ZM4.87901 2.84375C4.91704 2.76926 4.94875 2.69171 4.9738 2.61192L5.02797 2.44942C5.07726 2.30154 5.08863 2.27175 5.10001 2.25008C5.12925 2.19375 5.17128 2.14506 5.22274 2.1079C5.27419 2.07075 5.33363 2.04617 5.3963 2.03613C5.42067 2.03233 5.45263 2.03125 5.60863 2.03125H7.39072C7.54672 2.03125 7.57922 2.03233 7.60305 2.03667C7.66567 2.04665 7.72508 2.07115 7.77652 2.10821C7.82797 2.14527 7.87004 2.19386 7.89934 2.25008C7.91072 2.27175 7.92209 2.30154 7.97138 2.44996L8.02555 2.61246L8.04667 2.67313C8.0678 2.73217 8.09272 2.78904 8.12034 2.84375H4.87901Z" fill="#EF4D56" />
                                    <path d="M3.20338 4.57701C3.1962 4.46948 3.14659 4.36921 3.06548 4.29826C2.98437 4.2273 2.87839 4.19148 2.77086 4.19866C2.66333 4.20584 2.56306 4.25545 2.4921 4.33656C2.42115 4.41767 2.38532 4.52365 2.39251 4.63118L2.64384 8.39685C2.68988 9.09126 2.72726 9.65243 2.81501 10.0933C2.90655 10.5511 3.06146 10.9335 3.38213 11.233C3.70226 11.5331 4.09442 11.6626 4.55755 11.7232C5.0028 11.7812 5.56505 11.7812 6.26163 11.7812H6.73776C7.4338 11.7812 7.99659 11.7812 8.44184 11.7232C8.90442 11.6626 9.29659 11.5331 9.61726 11.233C9.93738 10.9335 10.0923 10.5505 10.1838 10.0933C10.2716 9.65297 10.3084 9.09126 10.355 8.39685L10.6063 4.63118C10.6135 4.52365 10.5777 4.41767 10.5067 4.33656C10.4358 4.25545 10.3355 4.20584 10.228 4.19866C10.1205 4.19148 10.0145 4.2273 9.93337 4.29826C9.85225 4.36921 9.80265 4.46948 9.79546 4.57701L9.5463 8.31451C9.49755 9.04414 9.46288 9.55222 9.38705 9.9341C9.31284 10.3051 9.20992 10.5012 9.06205 10.6399C8.91363 10.7786 8.71105 10.8685 8.33622 10.9172C7.95001 10.9676 7.44084 10.9687 6.70905 10.9687H6.2898C5.55855 10.9687 5.04938 10.9676 4.66263 10.9172C4.2878 10.8685 4.08521 10.7786 3.9368 10.6399C3.78892 10.5012 3.68601 10.3051 3.6118 9.9341C3.53596 9.55222 3.5013 9.04414 3.45255 8.31451L3.20338 4.57701Z" fill="#EF4D56" />
                                    <path d="M5.10499 5.55427C5.21215 5.54353 5.31919 5.57579 5.40258 5.64394C5.48597 5.7121 5.53889 5.81058 5.5497 5.91773L5.82053 8.62606C5.82846 8.73173 5.79479 8.83632 5.7267 8.91752C5.65861 8.99871 5.56149 9.05009 5.45606 9.06069C5.35062 9.07129 5.24522 9.04027 5.16233 8.97426C5.07944 8.90824 5.02562 8.81245 5.01236 8.70731L4.74153 5.99898C4.73079 5.89182 4.76304 5.78478 4.8312 5.70139C4.89935 5.61799 4.99783 5.56508 5.10499 5.55427ZM7.89457 5.55427C8.00162 5.56508 8.10001 5.61792 8.16815 5.70119C8.23629 5.78446 8.26862 5.89136 8.25803 5.99844L7.9872 8.70677C7.97376 8.81171 7.91993 8.90726 7.83714 8.97312C7.75435 9.03898 7.64913 9.06994 7.54386 9.05943C7.4386 9.04892 7.34158 8.99776 7.27345 8.91683C7.20532 8.8359 7.17144 8.73158 7.17903 8.62606L7.44986 5.91773C7.46067 5.81068 7.51351 5.71229 7.59678 5.64415C7.68005 5.57601 7.7875 5.54369 7.89457 5.55427Z" fill="#EF4D56" />
                                  </svg>
                                </i>
                              </button>
                            )}
                          </div>
                        </div>
                        <div className='right-deals-filtes'>
                          <div className='filter-deals-owner'>
                            {/* <div className="audience-type-dropdown mr-3">
                              <button className="btn-style-border" onClick={() => setAudienceTypeOpen(!audienceTypeOpen)}>
                                {formData['audience']?.['autoEnroll'] ? 'Dynamic Audience' : 'Static Audience'}
                                <svg width="4" height="4" viewBox="0 0 4 4" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: 6 }}>
                                  <path d="M2.99316 2.46804L1.91042 0.544434L0.307735 0.544434C0.0334783 0.544434 -0.10365 0.986291 0.090615 1.24531L1.57046 3.21843C1.80758 3.53459 2.19325 3.53459 2.43037 3.21843L2.99316 2.46804Z" fill="var(--primary-color)" />
                                  <path d="M3.69092 0.544434L1.91111 0.544434L2.99386 2.46804L3.9109 1.24531C4.10231 0.986291 3.96518 0.544434 3.69092 0.544434Z" fill="var(--primary-color)" />
                                </svg>
                              </button>
                              {audienceTypeOpen && (
                                <div className="audience-type-menu">
                                  <button onClick={() => { handleChange('audience', { ...formData['audience'], autoEnroll: true }); setAudienceTypeOpen(false); }}>
                                    Dynamic Audience
                                  </button>
                                  <button onClick={() => { handleChange('audience', { ...formData['audience'], autoEnroll: false }); setAudienceTypeOpen(false); }}>
                                    Static Audience
                                  </button>
                                </div>
                              )}
                            </div> */}
                            <button
                              size="small"
                              className="btn-style-white"
                              onClick={() => setShowAIDrawer(true)}
                              title='AI Prompts'
                            >
                              <i className="icon">
                                <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M5.92595 0.0252907L4.60173 0.0257634C3.3951 0.0257634 2.41325 1.00749 2.41325 2.21424C2.41325 2.37036 2.43122 2.52895 2.46667 2.6872C1.27375 3.06313 0.450033 4.17864 0.450033 5.43869C0.450033 5.9094 0.566559 6.37291 0.785076 6.78642C0.300652 7.26647 0 7.93183 0 8.66621C0 9.8648 0.800558 10.8794 1.89503 11.2046C1.85614 11.3547 1.83637 11.5092 1.83618 11.6642C1.83618 12.6808 2.66321 13.5077 3.67968 13.5077C3.79514 13.5077 3.91155 13.4961 4.02784 13.4736C4.0711 14.1972 4.67323 14.7726 5.40761 14.7726C6.16562 14.7726 6.78985 14.1595 6.78985 13.4034V0.889193C6.78973 0.412806 6.40245 0.0252907 5.92595 0.0252907ZM14.2721 8.64092C14.2721 7.90666 13.9716 7.2413 13.4869 6.76101C13.7057 6.34773 13.8223 5.88411 13.8223 5.41351C13.8223 4.15347 12.9984 3.03784 11.8054 2.66203C11.841 2.50378 11.8589 2.34506 11.8589 2.18907C11.8589 0.98232 10.877 0.000472724 9.67016 0.000472724L8.34594 0C7.86955 0 7.48215 0.387515 7.48215 0.864139V10.9098C7.48215 10.9209 7.47506 10.932 7.47506 10.9443C7.47506 10.9891 7.48215 11.0331 7.48215 11.0765V13.3877H7.48263C7.49811 14.1955 8.11147 14.7445 8.86416 14.7445C9.59854 14.7445 10.201 14.1704 10.2443 13.4469C10.3605 13.4696 10.4771 13.4818 10.5923 13.4818C11.6089 13.4818 12.4358 12.6552 12.4358 11.6388C12.4358 11.4827 12.4155 11.3289 12.3772 11.1794C13.4716 10.8541 14.2721 9.83951 14.2721 8.64092Z" fill="currentColor" />
                                </svg>
                              </i>
                            </button>
                            <button
                              size="small"
                              className="btn-style-border ml-3"
                              onClick={() => setImportWizardOpen(true)}

                            >
                              <i className="icon">
                                <svg width="14" height="10" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M10.4281 9.71086H7.5647V6.81751H8.51097C8.75095 6.81751 8.89275 6.54481 8.75095 6.34846L6.91022 3.80144C6.79296 3.63782 6.55026 3.63782 6.433 3.80144L4.59227 6.34846C4.45047 6.54481 4.58955 6.81751 4.83225 6.81751H5.77852V9.71086H2.57156C1.13989 9.63177 0 8.29281 0 6.84205C0 5.84124 0.542674 4.9686 1.34714 4.49683C1.27351 4.29776 1.23533 4.08505 1.23533 3.86144C1.23533 2.83881 2.06161 2.01253 3.08424 2.01253C3.30513 2.01253 3.51783 2.05071 3.7169 2.12433C4.30866 0.869914 5.5849 0 7.06839 0C8.9882 0.002727 10.5699 1.47258 10.7498 3.34603C12.2252 3.59964 13.346 4.96587 13.346 6.51208C13.346 8.16465 12.0588 9.59632 10.4281 9.71086Z" fill="currentColor" />
                                </svg>
                              </i>
                              Upload CSV
                            </button>
                            <button
                              size="small"
                              className="btn-style-new ml-3"
                              onClick={() => setShowFilterModal(true)}
                              title={allFilters.length > 0 ? `Filters (${allFilters.length})` : 'Add Filter'}
                            >
                              <i className="icon">
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M6.38173 13.2126C6.10173 13.2126 5.82757 13.1426 5.5709 13.0026C5.05756 12.7168 4.7484 12.1976 4.7484 11.6143V8.5226C4.7484 8.23093 4.5559 7.79343 4.37506 7.57176L2.1934 5.26176C1.8259 4.89426 1.5459 4.26426 1.5459 3.79176V2.4501C1.5459 1.51676 2.25173 0.787598 3.15007 0.787598H10.8501C11.7367 0.787598 12.4542 1.5051 12.4542 2.39176V3.6751C12.4542 4.2876 12.0867 4.98176 11.7426 5.32593L9.21673 7.5601C8.97173 7.76426 8.77923 8.21343 8.77923 8.5751V11.0834C8.77923 11.6026 8.45256 12.2034 8.04423 12.4484L7.23923 12.9676C6.97673 13.1309 6.67923 13.2126 6.38173 13.2126ZM3.15007 1.6626C2.74173 1.6626 2.4209 2.00676 2.4209 2.4501V3.79176C2.4209 4.0076 2.5959 4.4276 2.81756 4.64926L5.04007 6.98843C5.33757 7.35593 5.62923 7.96843 5.62923 8.51676V11.6084C5.62923 11.9876 5.89173 12.1743 6.00256 12.2326C6.24756 12.3668 6.54506 12.3668 6.77256 12.2268L7.5834 11.7076C7.74673 11.6084 7.91006 11.2934 7.91006 11.0834V8.5751C7.91006 7.95093 8.2134 7.2626 8.6509 6.8951L11.1476 4.68426C11.3459 4.48593 11.5851 4.01343 11.5851 3.66926V2.39176C11.5851 1.98926 11.2584 1.6626 10.8559 1.6626H3.15007Z" fill="currentColor" />
                                  <path d="M3.49989 6.27082C3.41823 6.27082 3.34239 6.24749 3.26656 6.20666C3.06239 6.07832 2.99823 5.80416 3.12656 5.59999L6.00239 0.991658C6.13073 0.787491 6.39906 0.723324 6.60323 0.851658C6.80739 0.979991 6.87156 1.24832 6.74323 1.45249L3.86739 6.06082C3.78573 6.19499 3.64573 6.27082 3.49989 6.27082Z" fill="currentColor" />
                                </svg>
                              </i>
                              {allFilters.length > 0 ? `Filters (${allFilters.length})` : 'Add Filter'}
                            </button>
                            {/* <button
                              size="small"
                              className="btn-style-new"
                              
                            >
                              <i className="icon">
                                
                              </i>
                              {allFilters.length > 0 ? `Filters (${allFilters.length})` : 'Add Filter'}
                            </button> */}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="campaigns-table">
                      {/* Active Filter Chips */}
                      {allFilters.length > 0 && (
                        <div className="active-filters-bar">
                          {allFilters.map(filter => {
                            // Format the filter value for display
                            let displayValue = filter['value'];
                            if (Array.isArray(displayValue)) {
                              displayValue = displayValue.join(', ');
                            } else if (typeof displayValue === 'object' && displayValue !== null) {
                              // For objects like {"M": true, "A": true}, extract the keys with truthy values
                              const trueKeys = Object.keys(displayValue).filter(key => displayValue[key]);
                              displayValue = displayValue['label'] || displayValue['name'] || (trueKeys.length > 0 ? trueKeys.join(', ') : JSON.stringify(displayValue));
                            }
                            return (
                              <Chip
                                size="small"
                                key={filter['id']}
                                variant="outlined"
                                label={`${filter['property']?.['label'] || filter['property']?.['name']} ${filter['operator']?.['label'] || ''} ${displayValue || ''}`}
                                onDelete={() => handleRemoveFilter(filter['groupId'], filter['id'])}
                              />
                            );
                          })}
                          <Button size="small" onClick={handleClearFilters}>Clear All</Button>
                        </div>
                      )}

                      {/* Contacts Table */}
                      {showContactsPreview && (
                        <div className="contacts-table-wrapper">
                          {contactsLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                              <TableAnimationFiles />
                            </Box>
                          ) : contacts.length === 0 ? (
                            <div className="contacts-empty" onClick={() => setImportWizardOpen(true)}>
                              <svg width="149" height="108" viewBox="0 0 149 108" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M116.041 107.911H84.1782V75.7586H94.7081C97.3785 75.7586 98.9564 72.7282 97.3785 70.5464L76.8953 42.243C75.5905 40.4248 72.8897 40.4248 71.5849 42.243L51.1017 70.5464C49.5237 72.7282 51.0714 75.7586 53.7721 75.7586H64.302V107.911H28.6157C12.6844 107.032 0 92.1527 0 76.0313C0 64.91 6.03874 55.2129 14.9906 49.9704C14.1713 47.7582 13.7465 45.3945 13.7465 42.9097C13.7465 31.5459 22.9411 22.3639 34.3207 22.3639C36.7787 22.3639 39.1456 22.7882 41.3608 23.6064C47.9458 9.6668 62.1474 0 78.6554 0C100.019 0.0303034 117.619 16.3639 119.622 37.1823C136.039 40.0005 148.511 55.1826 148.511 72.3646C148.511 90.7285 134.187 106.638 116.041 107.911Z" fill="#120348" />
                              </svg>
                              <h4 className="mt-4">Select a CSV File To Upload</h4>
                              <p>
                                {allFilters.length > 0
                                  ? 'Try adjusting your filters or clear them to see all contacts.'
                                  : 'Click contacts using the Upload CSV button.'}
                              </p>
                              {allFilters.length > 0 && <Button size="small" onClick={handleClearFilters}>Clear Filters</Button>}
                            </div>
                          ) : (
                            <>
                              <style>{tableStyles}</style>
                              <DataTable
                                columns={columns}
                                data={contacts}
                                fixedHeader
                                keyField="_id"
                                selectableRows
                                persistTableHead
                                pagination
                                paginationServer
                                paginationTotalRows={totalContacts}
                                paginationPerPage={rowsPerPage}
                                paginationDefaultPage={currentPage}
                                paginationResetDefaultPage={paginationResetToggle}
                                onChangePage={handlePageChange}
                                onChangeRowsPerPage={handlePerRowsChange}
                                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                fixedHeaderScrollHeight={headerScrollHeight}
                                className={`table-sellagent table-workflow audience-table ${responsiveClass}`}
                                progressPending={contactsLoading}
                                progressComponent={<TableAnimationFiles />}
                              />
                              {/* <table className="contacts-table">
                                <thead>
                                  <tr>
                                    <th style={{ width: 40 }}>
                                      <input
                                        type="checkbox"
                                        // checked={isAllVisibleSelected}
                                        // ref={(el) => {
                                        //   if (el) el.indeterminate = isSomeVisibleSelected; // middle state
                                        // }}
                                        // onChange={toggleAllVisible}
                                        aria-label="Select all"
                                      />
                                    </th>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Phone</th>
                                    <th>Company</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {displayedContacts.map(contact => (
                                    <tr key={contact['_id']}>
                                      <td>
                                        <input
                                          type="checkbox"
                                          // checked={selectedIds.has(contact["_id"])}
                                          // onChange={() => toggleOne(contact["_id"])}
                                          aria-label={`Select ${contact["firstName"] || ""} ${contact["lastName"] || ""}`.trim()}
                                        />
                                      </td>
                                      <td className="contact-name">
                                        {contact['firstName'] || ''} {contact['lastName'] || ''}
                                        {!contact['firstName'] && !contact['lastName'] && <span className="no-name">No name</span>}
                                      </td>
                                      <td>{contact['email'] || <span className="no-data">-</span>}</td>
                                      <td>{contact['phone'] || <span className="no-data">-</span>}</td>
                                      <td>{contact['company'] || <span className="no-data">-</span>}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table> */}
                              {/* {totalContacts > previewLimit && (
                                <div className="table-footer">
                                  <span>Showing {Math.min(previewLimit, contacts.length)} of {totalContacts.toLocaleString()} contacts</span>
                                  <Button size="small" onClick={handleLoadMore}>Load More</Button>
                                </div>
                              )} */}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Advanced Filter Modal */}
      <AdvancedFilterModal
        objectType="contacts"
        isOpen={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        onGetData={({ filters, filterDefinition }) => {
          dispatch(getObjects({ objectType: 'contacts', limit: 50, page: 1, filters }));
          // Save filters into formData immediately so autoSave captures them correctly
          handleChange('audience', { ...formData['audience'], filters, filterDefinition: filterDefinition || activeFilters });
        }}
      />

      {/* Import Wizard - Only render when open */}
      {importWizardOpen && (
        <div className="import-wizard-overlay">
          <ImportWizard
            objectType="contacts"
            onComplete={handleImportComplete}
            importWizardOpen={importWizardOpen}
            setImportWizardOpen={setImportWizardOpen}
          />
        </div>
      )}

      {/* AI Variables Drawer */}
      <AIVariablesDrawer
        open={showAIDrawer}
        onClose={() => setShowAIDrawer(false)}
        contacts={contacts}
        totalContactsCount={totalContacts}
        filters={formData['audience']?.['filters'] || {}}
        campaignId={campaignId}
        onColumnGenerated={({ columnName, results }) => {
          const map = {};
          results.forEach(r => { map[r.contactId] = r.value; });
          setAiColumns(prev => ({ ...prev, [columnName]: map }));
        }}
      />
    </div>
  )
}

const tableStyles = `
          .table-sellagent input[type="checkbox"] {
            width: 13px;
          height: 13px;
          cursor: pointer;
          accent-color: #cbd6e2;
          transform: scale(1);
          opacity: 0.45;
  }

          .table-sellagent input[type="checkbox"]:checked {
            opacity: 1;
          accent-color: var(--primary-color);
  }

          .rdt_TableRow {
            transition: background-color 0.15s ease;
  }

          .rdt_TableRow:hover {
            background - color: #f5f5f5;
  }

          .table-container {
            position: relative;
  }
          `;

export default AudienceStep